/**
 * Application configuration file
 *
 * This file centralizes all configuration values used throughout the application.
 * Change these values based on your environment (dev, staging, prod).
 */

// Company constants (static, environment-independent)
export const COMPANY = {
  name: 'Blended Chat',
  supportEmail: 'support@blendedchat.com',
} as const;

// Determine current environment
const ENV = import.meta.env.VITE_APP_ENV || 'development';

interface Config {
  baseUrl: string;
  media: {
    constraints: MediaStreamConstraints;
  };
  debug: boolean;
  facebook: {
    appId: string,
    oauth: {
      scope: string,
      jwtAuthKey: string,
      redirectUrl: string,
      refreshTokenCheckingInterval: number // !!!important - checking interval should be shorter than jwt token lifetime
    }
  },
  google: {
    clientId: string,
    oauth: {
      scope: string,
      jwtAuthKey: string,
      redirectUrl: string,
      refreshTokenCheckingInterval: number
    }
  },
  endpoints: {
    ws: string,
    auth: {
      facebook: {
        login: string,
        logout: string,
        verify: string,
        refreshToken: string
      },
      google: {
        login: string,
        logout: string,
        verify: string,
        refreshToken: string
      }
    },
    browserCheck: string,
    reporting: string,
    banEvidenceImages: string,
    createCheckoutSession: string,
    cancelSubscription: string,
    uncancelSubscription: string,
    updateGender: string,
    updateAgeGroup: string,
    deleteAccount: string
  },
  paymentProducts: {
    banLift: string,
    proSubscription: string
  }
}

// Base configurations for different environments
const configs: Record<string, Config> = {
  development: {
    baseUrl: import.meta.env.VITE_BACKEND_URL || 'http://localhost:8081',
    media: {
      constraints: {
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: true
      }
    },
    debug: true,
    facebook: {
      appId: import.meta.env.VITE_FACEBOOK_APP_ID,
      oauth: {
        scope: 'email,public_profile,user_age_range,user_gender,user_location,user_birthday',
        jwtAuthKey: import.meta.env.VITE_JWT_AUTH_KEY_NAME,
        redirectUrl: import.meta.env.VITE_FACEBOOK_OAUTH_REDIRECT_URL,
        refreshTokenCheckingInterval: 60000 // in ms (check every minute)
      }
    },
    google: {
      clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      oauth: {
        scope: 'openid email profile',
        jwtAuthKey: import.meta.env.VITE_JWT_AUTH_KEY_NAME,
        redirectUrl: import.meta.env.VITE_GOOGLE_OAUTH_REDIRECT_URL || import.meta.env.VITE_FACEBOOK_OAUTH_REDIRECT_URL,
        refreshTokenCheckingInterval: 60000
      }
    },
    endpoints: {
      ws: 'api/ws',
      auth: {
        facebook: {
          login: 'api/auth/oauth/facebook/login',
          logout: 'api/auth/oauth/facebook/logout',
          verify: 'api/auth/oauth/facebook/verify-token',
          refreshToken: 'api/auth/oauth/facebook/refresh-token'
        },
        google: {
          login: 'api/auth/oauth/google/login',
          logout: 'api/auth/oauth/google/logout',
          verify: 'api/auth/oauth/google/verify-token',
          refreshToken: 'api/auth/oauth/google/refresh-token'
        }
      },
      browserCheck: 'api/browser-check',
      reporting: 'api/reports',
      banEvidenceImages: 'api/ban-evidence-images',
      createCheckoutSession: 'api/payment/polarsh/create-checkout-session',
      cancelSubscription: 'api/payment/polarsh/cancel-subscription',
      uncancelSubscription: 'api/payment/polarsh/uncancel-subscription',
      updateGender: 'api/user/gender',
      updateAgeGroup: 'api/user/age-group',
      deleteAccount: 'api/user'
    },
    paymentProducts: {
      banLift: import.meta.env.VITE_POLAR_BAN_LIFT_PRODUCT_ID,
      proSubscription: import.meta.env.VITE_POLAR_PRO_SUBSCRIPTION_PRODUCT_ID
    }
  },

  staging: {
    baseUrl: import.meta.env.VITE_BACKEND_URL,
    media: {
      constraints: {
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: true
      }
    },
    debug: true,
    facebook: {
      appId: import.meta.env.VITE_FACEBOOK_APP_ID,
      oauth: {
        scope: 'email,public_profile',
        jwtAuthKey: import.meta.env.VITE_JWT_AUTH_KEY_NAME,
        redirectUrl: import.meta.env.VITE_FACEBOOK_OAUTH_REDIRECT_URL,
        refreshTokenCheckingInterval: 60000 // in ms
      }
    },
    google: {
      clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      oauth: {
        scope: 'openid email profile',
        jwtAuthKey: import.meta.env.VITE_JWT_AUTH_KEY_NAME,
        redirectUrl: import.meta.env.VITE_GOOGLE_OAUTH_REDIRECT_URL || import.meta.env.VITE_FACEBOOK_OAUTH_REDIRECT_URL,
        refreshTokenCheckingInterval: 60000
      }
    },
    endpoints: {
      ws: 'api/ws',
      auth: {
        facebook: {
          login: 'api/auth/oauth/facebook/login',
          logout: 'api/auth/oauth/facebook/logout',
          verify: 'api/auth/oauth/facebook/verify-token',
          refreshToken: 'api/auth/oauth/facebook/refresh-token'
        },
        google: {
          login: 'api/auth/oauth/google/login',
          logout: 'api/auth/oauth/google/logout',
          verify: 'api/auth/oauth/google/verify-token',
          refreshToken: 'api/auth/oauth/google/refresh-token'
        }
      },
      browserCheck: 'api/browser-check',
      reporting: 'api/reports',
      banEvidenceImages: 'api/ban-evidence-images',
      createCheckoutSession: 'api/payment/polarsh/create-checkout-session',
      cancelSubscription: 'api/payment/polarsh/cancel-subscription',
      uncancelSubscription: 'api/payment/polarsh/uncancel-subscription',
      updateGender: 'api/user/gender',
      updateAgeGroup: 'api/user/age-group',
      deleteAccount: 'api/user'
    },
    paymentProducts: {
      banLift: import.meta.env.VITE_POLAR_BAN_LIFT_PRODUCT_ID,
      proSubscription: import.meta.env.VITE_POLAR_PRO_SUBSCRIPTION_PRODUCT_ID
    }
  },

  production: {
    baseUrl: import.meta.env.VITE_BACKEND_URL,
    media: {
      constraints: {
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: true
      }
    },
    debug: false,
    facebook: {
      appId: import.meta.env.VITE_FACEBOOK_APP_ID,
      oauth: {
        scope: 'email,public_profile,user_age_range,user_gender,user_location,user_birthday',
        jwtAuthKey: import.meta.env.VITE_JWT_AUTH_KEY_NAME,
        redirectUrl: import.meta.env.VITE_FACEBOOK_OAUTH_REDIRECT_URL,
        refreshTokenCheckingInterval: 60000 // in ms
      }
    },
    google: {
      clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      oauth: {
        scope: 'openid email profile',
        jwtAuthKey: import.meta.env.VITE_JWT_AUTH_KEY_NAME,
        redirectUrl: import.meta.env.VITE_GOOGLE_OAUTH_REDIRECT_URL || import.meta.env.VITE_FACEBOOK_OAUTH_REDIRECT_URL,
        refreshTokenCheckingInterval: 60000
      }
    },
    endpoints: {
      ws: 'api/ws',
      auth: {
        facebook: {
          login: 'api/auth/oauth/facebook/login',
          logout: 'api/auth/oauth/facebook/logout',
          verify: 'api/auth/oauth/facebook/verify-token',
          refreshToken: 'api/auth/oauth/facebook/refresh-token'
        },
        google: {
          login: 'api/auth/oauth/google/login',
          logout: 'api/auth/oauth/google/logout',
          verify: 'api/auth/oauth/google/verify-token',
          refreshToken: 'api/auth/oauth/google/refresh-token'
        }
      },
      browserCheck: 'api/browser-check',
      reporting: 'api/reports',
      banEvidenceImages: 'api/ban-evidence-images',
      createCheckoutSession: 'api/payment/polarsh/create-checkout-session',
      cancelSubscription: 'api/payment/polarsh/cancel-subscription',
      uncancelSubscription: 'api/payment/polarsh/uncancel-subscription',
      updateGender: 'api/user/gender',
      updateAgeGroup: 'api/user/age-group',
      deleteAccount: 'api/user'
    },
    paymentProducts: {
      banLift: import.meta.env.VITE_POLAR_BAN_LIFT_PRODUCT_ID,
      proSubscription: import.meta.env.VITE_POLAR_PRO_SUBSCRIPTION_PRODUCT_ID
    }
  }
};

// Default to development if environment not found
const config = configs[ENV] || configs.development;
export default config;